import { PlusCircleOutlined } from '@ant-design/icons';
import { Button, Col, Row, Space } from 'antd';
import { useFormikContext } from 'formik';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { WebPageBlockType } from '../../../../api/types';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import { type ColumnBlockMainInput, type WebpageFormikValue } from '../shared';
import ColumnListBlock, { colSpan } from './ColumnListBlock';
import ReorderingButtons, { getNewBlock } from './shared';

type ColumnBlockProps = {
    name: string;
    index: number;
    disabled?: boolean;
};

const ColumnBlock = ({ name, index, disabled = false }: ColumnBlockProps) => {
    const { t } = useTranslation(['webpageDetails']);
    const { values, setFieldValue } = useFormikContext<WebpageFormikValue>();
    const onSelect = useCallback(
        (value, options) => {
            values.webPageBlock[index] = getNewBlock(value);
            setFieldValue(name, getNewBlock(value));
        },
        [index, name, setFieldValue, values.webPageBlock]
    );

    const addImageDescriptions = useCallback(() => {
        const columnBlock = values.webPageBlock[index] as ColumnBlockMainInput;
        if (values.webPageBlock[index].type !== WebPageBlockType.Column) {
            throw new Error('WebPageBlockType is not same as input');
        }

        setFieldValue(`${name}.columns`, [
            ...columnBlock.columns,
            {
                title: { defaultValue: '', overrides: [] },
                description: { defaultValue: '', overrides: [] },
            },
        ]);
    }, [values.webPageBlock, index, setFieldValue, name]);

    const { webpageBlockType } = useSystemOptions();

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('webpageDetails:fields.column.blockName')}>
                <Panel
                    key={t('webpageDetails:fields.column.blockName')}
                    extra={
                        !disabled && <ReorderingButtons index={index} setFieldValue={setFieldValue} values={values} />
                    }
                    header={t('webpageDetails:fields.column.blockName')}
                >
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('webpageDetails:fields.blockType', { returnObjects: true })}
                                disabled={disabled}
                                name={`${name}.type`}
                                onSelect={onSelect}
                                options={webpageBlockType}
                                showSearch
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('webpageDetails:fields.column.title', { returnObjects: true })}
                                disabled={disabled}
                                name={`${name}.title`}
                            />
                        </Col>
                    </Row>
                    <ColumnListBlock disabled={disabled} name={`${name}.columns`} />
                    {!disabled && (
                        <Button icon={<PlusCircleOutlined />} onClick={addImageDescriptions} type="link">
                            {t('webpageDetails:actions.add')}
                        </Button>
                    )}
                </Panel>
            </CollapsibleWrapper>
        </Space>
    );
};

export default ColumnBlock;
